// useDatabaseFormsState.js

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';

import {
  buildFormSettingsDraft,
  buildVisibleFormsForSettings,
  canSaveFormSettingsDraft,
  isVirtualFormDoc,
} from 'collection/settings/forms/formSettingsUtils';
import { subscribeToForms } from 'services/forms/FormReadService';
import {
  createDefaultDetailsForm,
  createForm,
  deleteFormByKey,
  refreshDefaultFormBootstrap,
  updateForm,
} from 'services/forms/FormWriteService';
import { isSystemTypeForm } from 'services/forms/TypeFormService';
import { getFirstNodeForDatabase } from 'services/nodes/NodeReadService';
import { getMovedLayoutPosition, getNextLayoutPosition } from 'utilities/LayoutOrder';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function findPersistedForm(forms, formKey) {
  const cleanedFormKey = normalizeString(formKey);
  if (!cleanedFormKey) return null;
  return forms.find((form) => form.key === cleanedFormKey) || null;
}

/**
 * Manages type-scoped form templates for the Database Settings Forms tab.
 */
export default function useDatabaseFormsState({
  isOpen,
  previewNodeKeyOverride = '',
  typeDef = null,
  typeKey = '',
  userKey = null,
  baseKey,
}) {
  const [forms, setForms] = useState([]);
  const [previewNodeKey, setPreviewNodeKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createTitleDraft, setCreateTitleDraft] = useState('');
  const [formDesignOpen, setFormDesignOpen] = useState(false);
  const [formSettingsOpen, setFormSettingsOpen] = useState(false);
  const [editingFormKey, setEditingFormKey] = useState('');
  const [formSettingsDraft, setFormSettingsDraft] = useState(null);
  const [savedFormSettingsDraft, setSavedFormSettingsDraft] = useState(null);
  const [editingFormDoc, setEditingFormDoc] = useState(null);
  const lastInitializedTypeKeyRef = useRef('');

  const resolvedTypeKey = useMemo(() => normalizeString(typeKey), [typeKey]);
  const resolvedBaseKey = useMemo(() => normalizeString(baseKey), [baseKey]);
  const resolvedUserKey = useMemo(() => normalizeString(userKey), [userKey]);

  const visibleForms = useMemo(
    () => buildVisibleFormsForSettings(forms),
    [forms],
  );

  const editingForm = useMemo(() => {
    if (editingFormDoc?.key && editingFormDoc.key === editingFormKey) {
      return editingFormDoc;
    }

    const persisted = findPersistedForm(forms, editingFormKey);
    if (persisted) {
      return persisted;
    }

    return visibleForms.find((form) => form.key === editingFormKey) || null;
  }, [editingFormDoc, editingFormKey, forms, visibleForms]);

  useEffect(() => {
    if (!editingFormKey) {
      setEditingFormDoc(null);
      return;
    }

    const persisted = findPersistedForm(forms, editingFormKey);
    if (persisted) {
      setEditingFormDoc(persisted);
    }
  }, [editingFormKey, forms]);

  const canSaveFormSettings = useMemo(() => (
    canSaveFormSettingsDraft(formSettingsDraft, savedFormSettingsDraft) && !isSaving
  ), [formSettingsDraft, isSaving, savedFormSettingsDraft]);

  useEffect(() => {
    if (!isOpen || !resolvedBaseKey || !resolvedTypeKey) {
      setForms([]);
      return undefined;
    }

    return subscribeToForms({
      typeKey: resolvedTypeKey,
      baseKey: resolvedBaseKey,
    }, setForms);
  }, [isOpen, resolvedTypeKey, resolvedBaseKey]);

  useEffect(() => {
    if (!isOpen) {
      lastInitializedTypeKeyRef.current = '';
      setPreviewNodeKey('');
      setCreateModalOpen(false);
      setFormSettingsOpen(false);
      setFormDesignOpen(false);
      setEditingFormKey('');
      setEditingFormDoc(null);
      setFormSettingsDraft(null);
      setSavedFormSettingsDraft(null);
      return;
    }

    if (lastInitializedTypeKeyRef.current === resolvedTypeKey) {
      return;
    }

    lastInitializedTypeKeyRef.current = resolvedTypeKey;
    setCreateTitleDraft('');
    setCreateModalOpen(false);
    setFormSettingsOpen(false);
    setFormDesignOpen(false);
    setEditingFormKey('');
    setFormSettingsDraft(null);
    setSavedFormSettingsDraft(null);
  }, [isOpen, resolvedTypeKey]);

  useEffect(() => {
    if (!isOpen || !resolvedBaseKey || !resolvedTypeKey) {
      setPreviewNodeKey('');
      return undefined;
    }

    const overridePreviewNodeKey = normalizeString(previewNodeKeyOverride);
    if (overridePreviewNodeKey) {
      setPreviewNodeKey(overridePreviewNodeKey);
      return undefined;
    }

    let cancelled = false;

    getFirstNodeForDatabase({
      type: resolvedTypeKey,
      baseKey: resolvedBaseKey,
    }).then((node) => {
      if (cancelled) return;
      setPreviewNodeKey(normalizeString(node?.key));
    }).catch(() => {
      if (cancelled) return;
      setPreviewNodeKey('');
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, previewNodeKeyOverride, resolvedTypeKey, resolvedBaseKey]);

  const ensureFormPersisted = useCallback(async (form) => {
    const cleanedFormKey = normalizeString(form?.key);
    if (!cleanedFormKey || !resolvedBaseKey || !resolvedTypeKey || !resolvedUserKey) {
      return null;
    }

    if (isSystemTypeForm(form)) {
      return form;
    }

    const persisted = findPersistedForm(forms, cleanedFormKey);
    if (persisted && !isVirtualFormDoc(persisted)) {
      if (isSystemTypeForm(persisted)) return persisted;
      return persisted;
    }

    if (form?.isDefault || cleanedFormKey === 'details') {
      // Do not materialize a base Details form when a system template already owns it.
      if (forms.some((entry) => isSystemTypeForm(entry) && (
        entry?.isDefault || normalizeString(entry?.key) === 'details'
      ))) {
        return forms.find((entry) => isSystemTypeForm(entry) && (
          entry?.isDefault || normalizeString(entry?.key) === 'details'
        )) || null;
      }

      const created = await createDefaultDetailsForm({
        typeDef,
        typeKey: resolvedTypeKey,
        userKey: resolvedUserKey,
        baseKey: resolvedBaseKey,
      });
      return refreshDefaultFormBootstrap(created, {
        typeDef,
        userKey: resolvedUserKey,
      });
    }

    const persistedForm = findPersistedForm(forms, cleanedFormKey);
    if (persistedForm?.isDefault && !persistedForm?.layoutEdited) {
      return refreshDefaultFormBootstrap(persistedForm, {
        typeDef,
        userKey: resolvedUserKey,
      });
    }

    return persistedForm;
  }, [forms, resolvedTypeKey, resolvedUserKey, resolvedBaseKey, typeDef]);

  const openFormDesign = useCallback(async (form) => {
    if (isSystemTypeForm(form)) {
      toast.error('System forms are edited in Admin → Types → Forms.');
      return;
    }

    try {
      const resolvedForm = await ensureFormPersisted(form);
      const cleanedFormKey = normalizeString(resolvedForm?.key || form?.key);
      if (!cleanedFormKey) return;
      if (isSystemTypeForm(resolvedForm)) {
        toast.error('System forms are edited in Admin → Types → Forms.');
        return;
      }

      setEditingFormDoc(resolvedForm || form);
      setEditingFormKey(cleanedFormKey);
      setFormDesignOpen(true);
    } catch (error) {
      toast.error(error?.message || 'Could not open form designer.');
    }
  }, [ensureFormPersisted]);

  const openFormSettings = useCallback(async (form) => {
    if (isSystemTypeForm(form)) {
      toast.error('System forms are edited in Admin → Types → Forms.');
      return;
    }

    try {
      const resolvedForm = await ensureFormPersisted(form);
      const cleanedFormKey = normalizeString(resolvedForm?.key || form?.key);
      if (!cleanedFormKey) return;
      if (isSystemTypeForm(resolvedForm)) {
        toast.error('System forms are edited in Admin → Types → Forms.');
        return;
      }

      const nextDraft = buildFormSettingsDraft(resolvedForm || form);
      setEditingFormDoc(resolvedForm || form);
      setEditingFormKey(cleanedFormKey);
      setFormSettingsDraft(nextDraft);
      setSavedFormSettingsDraft(nextDraft);
      setFormSettingsOpen(true);
    } catch (error) {
      toast.error(error?.message || 'Could not open form settings.');
    }
  }, [ensureFormPersisted]);

  const closeFormDesign = useCallback(() => {
    setFormDesignOpen(false);
    setEditingFormKey('');
    setEditingFormDoc(null);
  }, []);

  const handleAddForm = useCallback(() => {
    setCreateTitleDraft('');
    setCreateModalOpen(true);
  }, []);

  const handleCreateForm = useCallback(async () => {
    if (!resolvedBaseKey || !resolvedTypeKey || !resolvedUserKey || isCreating) {
      return false;
    }

    const cleanedTitle = normalizeString(createTitleDraft);
    if (!cleanedTitle) {
      return false;
    }

    setIsCreating(true);

    try {
      const nextForm = await createForm({
        position: getNextLayoutPosition(forms),
        title: cleanedTitle,
        typeKey: resolvedTypeKey,
        userKey: resolvedUserKey,
        baseKey: resolvedBaseKey,
      });

      if (!normalizeString(nextForm?.key)) {
        throw new Error('Form template could not be created.');
      }

      setCreateModalOpen(false);
      setCreateTitleDraft('');
      toast.success('Form template created.');
      return true;
    } catch (error) {
      toast.error(error?.message || 'Could not create form template.');
      return false;
    } finally {
      setIsCreating(false);
    }
  }, [
    createTitleDraft,
    forms,
    isCreating,
    resolvedTypeKey,
    resolvedUserKey,
    resolvedBaseKey,
  ]);

  const handleSaveFormSettings = useCallback(async () => {
    const cleanedFormKey = normalizeString(formSettingsDraft?.key);
    if (!cleanedFormKey || !canSaveFormSettings || !resolvedBaseKey || !resolvedTypeKey) {
      return;
    }

    setIsSaving(true);

    try {
      const title = normalizeString(formSettingsDraft?.title);
      await updateForm(cleanedFormKey, {
        description: normalizeString(formSettingsDraft?.description),
        title,
        titleLower: title.toLowerCase(),
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
      }, {
        typeKey: resolvedTypeKey,
        userKey: resolvedUserKey,
        baseKey: resolvedBaseKey,
      });

      const savedDraft = buildFormSettingsDraft({
        ...formSettingsDraft,
        title,
      });
      setFormSettingsDraft(savedDraft);
      setSavedFormSettingsDraft(savedDraft);
      toast.success('Form template saved.');
    } catch (error) {
      toast.error(error?.message || 'Could not save form template.');
    } finally {
      setIsSaving(false);
    }
  }, [
    canSaveFormSettings,
    formSettingsDraft,
    resolvedTypeKey,
    resolvedUserKey,
    resolvedBaseKey,
  ]);

  const handleDeleteForm = useCallback(async () => {
    const cleanedFormKey = normalizeString(formSettingsDraft?.key);
    if (!cleanedFormKey || formSettingsDraft?.isDefault || !resolvedBaseKey || !resolvedTypeKey) {
      return;
    }

    setIsSaving(true);

    try {
      await deleteFormByKey(cleanedFormKey, {
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
      });
      setFormSettingsOpen(false);
      setEditingFormKey('');
      setEditingFormDoc(null);
      setFormSettingsDraft(null);
      setSavedFormSettingsDraft(null);
      toast.success('Form template deleted.');
    } catch (error) {
      toast.error(error?.message || 'Could not delete form template.');
    } finally {
      setIsSaving(false);
    }
  }, [formSettingsDraft, resolvedTypeKey, resolvedBaseKey]);

  const handleReorderForm = useCallback(async (draggedFormKey, targetFormKey) => {
    const nextPosition = getMovedLayoutPosition(forms, draggedFormKey, targetFormKey);
    if (nextPosition == null) return;

    try {
      await updateForm(draggedFormKey, {
        position: nextPosition,
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
      }, {
        typeKey: resolvedTypeKey,
        userKey: resolvedUserKey,
        baseKey: resolvedBaseKey,
      });
    } catch (error) {
      toast.error(error?.message || 'Could not reorder form templates.');
      throw error;
    }
  }, [forms, resolvedTypeKey, resolvedUserKey, resolvedBaseKey]);

  return {
    canSaveFormSettings,
    closeFormDesign,
    createModalOpen,
    createTitleDraft,
    editingForm,
    formDesignOpen,
    formSettingsDraft,
    formSettingsOpen,
    forms: visibleForms,
    handleAddForm,
    handleCreateForm,
    handleDeleteForm,
    handleReorderForm,
    handleSaveFormSettings,
    isCreating,
    isSaving,
    openFormDesign,
    openFormSettings,
    previewNodeKey,
    setCreateModalOpen,
    setCreateTitleDraft,
    setFormSettingsDraft,
    setFormSettingsOpen,
  };
}
